import { useAsyncGetForeignCurrencyRates } from '@/context/services/foreign_currency/Detail/ApCurrencyRates.service';
import { InvoiceTransaction } from '@/features/InvoiceReport/Edit/type';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { ApPayeeDetail } from 'ap-openapi';
import { toISODateString } from 'date-util';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

export const useUpdateRateValue = ({
  getIndex,
  selectedPayee,
}: {
  getIndex: (t: InvoiceTransaction) => number;
  selectedPayee: ApPayeeDetail | undefined;
}) => {
  const [loading, setLoading] = useState<Record<number, boolean>>({});
  const { setValue } = useFormContext();
  const getForeignCurrencyRate = useAsyncGetForeignCurrencyRates();
  const { send } = useErrorTrackingSystem();

  const trigger = useCallback(
    async (changedInvoice: InvoiceTransaction) => {
      const index = getIndex(changedInvoice);

      if (
        changedInvoice.isCurrencyJPY() ||
        !selectedPayee ||
        !changedInvoice.dealDate
      ) {
        setValue(`invoiceTransactions.${index}.jpyRate`, 1);
        return;
      }

      setLoading((prev) => ({ ...prev, [index]: true }));

      try {
        const data = await getForeignCurrencyRate({
          currency: changedInvoice.currency,
          transactionDate: changedInvoice.dealDate
            ? toISODateString(changedInvoice.dealDate)
            : undefined,
        });
        setValue(
          `invoiceTransactions.${index}.jpyRate`,
          data?.ap_currency_rates?.[0]?.value ?? 1
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        send(error as Error);
        setValue(`invoiceTransactions.${index}.jpyRate`, 1);
      } finally {
        setLoading((prev) => ({ ...prev, [index]: false }));
      }
    },
    [getForeignCurrencyRate, getIndex, selectedPayee, send, setValue]
  );

  return {
    triggerRateValueSupporter: trigger,
    loading,
  };
};
